import React, { InputHTMLAttributes, useEffect, useRef, useState } from 'react'
import { useField } from 'formik'
import {
  InputStyled,
  InputWrapperStyled,
  InputStyledProps,
  InputSlot,
  InlineInputWrapper,
  InlineInput,
} from './styled'
import { Label } from '@components/Form/Label'
import {
  InputFeedbackStatus,
  InputFeedback,
} from '@components/Basic/InputFeedback'
import { FloatingLabelStyled } from '@components/Form/InputSmartSelect/styled'

export interface InputFieldAttrProps
  extends InputHTMLAttributes<HTMLInputElement> {
  $status?: InputFeedbackStatus
  name: string
  type: 'text' | 'email' | 'number' | 'password' | 'tel' | 'url'
  $direction?: InputStyledProps['$direction']
  $fluid?: boolean
  slotLeft?: React.ReactNode
  slotRight?: React.ReactNode
  label?: string
  $hasMargin?: boolean
  defaultLabel?: string
  floatingLabel?: string
  fieldIsUnavailable?: boolean
  inline?: boolean
}

export const InputField: React.FC<
  InputFieldAttrProps & {
    inputRefCallback?: (input: HTMLInputElement | null) => void
  }
> = ({
  label,
  slotLeft,
  slotRight,
  $status,
  $direction,
  $fluid,
  $hasMargin,
  defaultLabel,
  floatingLabel,
  fieldIsUnavailable,
  inputRefCallback,
  inline = false,
  ...props
}) => {
  const inputId = `${props.name}`
  const [isFocused, setIsFocused] = useState(false)
  const [hasValue, setHasValue] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const [field, meta] = inline ? useField(props.name) : [null, null]
  const hasError = inline && meta ? meta.touched && meta.error : false

  useEffect(() => {
    if (inputRefCallback && inputRef.current) {
      inputRefCallback(inputRef.current)
    }
  }, [inputRef.current, inputRefCallback])

  const handleFocus = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    setIsFocused(true)
    if (props.onFocus) {
      props.onFocus(event)
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    setIsFocused(false)
    if (props.onBlur) {
      props.onBlur(event)
    }
  }

  useEffect(() => {
    if (inputRef.current?.value) {
      setHasValue(true)
    } else {
      setHasValue(false)
    }
  }, [inputRef.current?.value])

  if (inline) {
    return (
      <InlineInputWrapper>
        <InlineInput
          {...(field || {})}
          {...props}
          ref={inputRef}
          $hasError={Boolean(hasError)}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </InlineInputWrapper>
    )
  }

  return (
    <InputStyled
      $direction={$direction || 'column'}
      $status={$status}
      type={props.type}
      name={props.name}
      id={inputId}
      $hasMargin={$hasMargin}
      className={props.className}
    >
      {label && (
        <Label $disabled={props.disabled} htmlFor={inputId} $label={label} />
      )}
      <InputWrapperStyled>
        {floatingLabel && (
          <FloatingLabelStyled
            htmlFor={inputId}
            isFloating={isFocused || hasValue}
            onClick={() => inputRef?.current?.focus()}
          >
            {isFocused || hasValue ? floatingLabel.toUpperCase() : defaultLabel}
          </FloatingLabelStyled>
        )}
        {slotLeft ? <InputSlot $slot={'left'}>{slotLeft}</InputSlot> : null}
        <input
          {...props}
          ref={inputRef}
          type={props.type}
          name={props.name}
          id={inputId}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={
            defaultLabel && floatingLabel && (isFocused || hasValue)
              ? {
                  paddingTop: '.6rem',
                  paddingBottom: '.1rem',
                  marginTop: '.6rem',
                  height: '34px',
                  color: (fieldIsUnavailable && 'red') || undefined,
                }
              : defaultLabel && floatingLabel && !isFocused && !hasValue
              ? {
                  height: '43px',
                }
              : {}
          }
        />
        {slotRight ? <InputSlot $slot={'right'}>{slotRight}</InputSlot> : null}
      </InputWrapperStyled>

      {$status && <InputFeedback $status={$status} />}
    </InputStyled>
  )
}
